/**
 * ============================================================
 * KEJJA OS — src/pages/library/ImportDocumentModal.tsx
 * Import a library item from URL, pasted text, or PDF.
 * ============================================================
 */

import { useState, useRef, type ChangeEvent } from 'react';
import {
  X, Globe, FileText, FileUp, Sparkles, Check, AlertCircle,
  FileCheck2, BookOpen,
} from 'lucide-react';

import { api } from '@/lib/api';
import {
  useCreateItem, useUploadAttachment, useFolders,
} from '@/hooks/useLibrary';
import type { LibraryDocType } from '@/lib/libraryApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// ============================================================
// Types
// ============================================================

interface ParsedDraft {
  title: string;
  body: string | null;
  docType: LibraryDocType;
  tags: string[];
  metadata: Record<string, unknown>;
  sourceUrl?: string;
  confidence: 'high' | 'medium' | 'low';
}

type Mode = 'url' | 'paste' | 'pdf';

const DOC_TYPES: Array<{ value: LibraryDocType; label: string; emoji: string }> = [
  { value: 'note', label: 'Note', emoji: '📝' },
  { value: 'id_document', label: 'ID', emoji: '🪪' },
  { value: 'receipt', label: 'Receipt', emoji: '🧾' },
  { value: 'medical', label: 'Medical', emoji: '🏥' },
  { value: 'school', label: 'School', emoji: '🎓' },
  { value: 'certificate', label: 'Certificate', emoji: '📜' },
  { value: 'contract', label: 'Contract', emoji: '✍️' },
  { value: 'link', label: 'Link', emoji: '🔗' },
  { value: 'other', label: 'Other', emoji: '📦' },
];

// ============================================================
// Component
// ============================================================

interface Props {
  homeId: string;

  ownerScope: 'shared' | 'personal';
  onClose: () => void;
}

export default function ImportDocumentModal({ homeId, ownerScope, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('pdf');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const [isParsing, setIsParsing] = useState(false);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<ParsedDraft | null>(null);

  const createItem = useCreateItem(homeId);
  const uploadAtt = useUploadAttachment(homeId);
  const { data: foldersData } = useFolders(homeId);

  const fileRef = useRef<HTMLInputElement>(null);

  const folders = (foldersData?.folders ?? []).filter((f) =>
    ownerScope === 'personal' ? f.ownerUserId !== null : f.ownerUserId === null,
  );

  const handleParse = async () => {
    setParseErr(null);
    setDraft(null);
    setIsParsing(true);
    try {
      const payload: Record<string, unknown> = {};
      if (mode === 'url') {
        if (!url.trim()) { setParseErr('Enter a URL.'); setIsParsing(false); return; }
        payload.url = url.trim();
      } else if (mode === 'paste') {
        if (!text.trim()) { setParseErr('Paste some text.'); setIsParsing(false); return; }
        payload.text = text;
      } else if (mode === 'pdf') {
        if (!pdfFile) { setParseErr('Pick a PDF.'); setIsParsing(false); return; }
        if (pdfFile.size > 10 * 1024 * 1024) {
          setParseErr('PDF is over 10 MB — pick a smaller file.');
          setIsParsing(false); return;
        }
        payload.pdfBase64 = await fileToBase64(pdfFile);
        payload.fileName = pdfFile.name;
      }

      const result = await api.post<{ draft: ParsedDraft }>(
        `/homes/${homeId}/library/documents/parse`, payload,
      );
      setDraft(result.draft);
    } catch (err) {
      setParseErr(err instanceof Error ? err.message : 'Parse failed');
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-2xl my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              Import document
            </h2>
            <Button type="button" size="sm" variant="ghost" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {!draft ? (
            <>
              <div className="flex gap-2">
                <ModeButton icon={FileUp} label="Upload PDF" active={mode === 'pdf'} onClick={() => setMode('pdf')} />
                <ModeButton icon={FileText} label="Paste text" active={mode === 'paste'} onClick={() => setMode('paste')} />
                <ModeButton icon={Globe} label="From URL" active={mode === 'url'} onClick={() => setMode('url')} />
              </div>

              {mode === 'pdf' && (
                <div>
                  <Label className="text-xs">Upload a document</Label>
                  <div className="mt-1 flex gap-2 items-center">
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                      <FileUp className="w-3.5 h-3.5 mr-1" />
                      {pdfFile ? 'Change file' : 'Pick PDF'}
                    </Button>
                    {pdfFile && (
                      <span className="text-xs text-muted-foreground truncate">
                        {pdfFile.name} · {(pdfFile.size / 1024).toFixed(0)} KB
                      </span>
                    )}
                    <input
                      ref={fileRef} type="file" accept="application/pdf"
                      className="hidden"
                      onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    We'll extract text, guess a doc type, and let you edit everything before saving.
                  </p>
                </div>
              )}

              {mode === 'paste' && (
                <div>
                  <Label className="text-xs">Paste document text</Label>
                  <textarea
                    autoFocus value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Paste anything — receipt text, medical notes, contract clauses…"
                    className="w-full mt-1 min-h-[200px] rounded-md border border-border bg-background p-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:border-purple-500"
                  />
                </div>
              )}

              {mode === 'url' && (
                <div>
                  <Label className="text-xs">URL</Label>
                  <Input
                    autoFocus value={url}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                    placeholder="https://…"
                    className="mt-1"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    We'll fetch the page, extract text, and create a note.
                  </p>
                </div>
              )}

              {parseErr && (
                <div className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 rounded-md p-2">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{parseErr}</span>
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-border">
                <Button
                  onClick={handleParse}
                  disabled={isParsing}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex-1"
                >
                  {isParsing ? 'Parsing…' : 'Parse document'}
                </Button>
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
              </div>
            </>
          ) : (
            <DraftReview
              ownerScope={ownerScope}
              folders={folders}
              pdfFile={mode === 'pdf' ? pdfFile : null}
              draft={draft}
              onDraftChange={setDraft}
              onBack={() => setDraft(null)}
              onSaved={onClose}
              createItem={createItem}
              uploadAtt={uploadAtt}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Mode button
// ============================================================

function ModeButton({
  icon: Icon, label, active, onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-colors',
        active
          ? 'border-purple-500/60 bg-purple-500/10 text-purple-700 dark:text-purple-300'
          : 'border-border text-muted-foreground',
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

// ============================================================
// DraftReview — edit & save
// ============================================================

function DraftReview({
  ownerScope, folders, pdfFile, draft, onDraftChange,
  onBack, onSaved, createItem, uploadAtt,
}: {

  ownerScope: 'shared' | 'personal';
  folders: Array<{ id: string; name: string; icon: string | null; ownerUserId: string | null }>;
  pdfFile: File | null;
  draft: ParsedDraft;
  onDraftChange: (d: ParsedDraft) => void;
  onBack: () => void;
  onSaved: () => void;
  createItem: ReturnType<typeof useCreateItem>;
  uploadAtt: ReturnType<typeof useUploadAttachment>;
}) {
  const [folderId, setFolderId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState(draft.tags.join(', '));
  const [isSaving, setIsSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const confidenceBadge = draft.confidence === 'high'
    ? { label: 'High confidence', cls: 'border-emerald-500/60 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10' }
    : draft.confidence === 'medium'
    ? { label: 'Medium confidence', cls: 'border-amber-500/60 text-amber-700 dark:text-amber-300 bg-amber-500/10' }
    : { label: 'Low confidence — check the type', cls: 'border-rose-500/60 text-rose-700 dark:text-rose-300 bg-rose-500/10' };

  const handleSave = async () => {
    setErr(null);
    setIsSaving(true);
    try {
      const tags = tagInput.split(',').map((t) => t.trim()).filter(Boolean);
      // 1. Create the item
      const result = await createItem.mutateAsync({
        title: draft.title.trim() || 'Untitled',
        body: draft.body,
        docType: draft.docType,
        tags,
        metadata: draft.metadata,
        folderId,
        ownerUserId: ownerScope === 'personal' ? undefined : null,
      });

      // 2. If we have a PDF, attach it
      if (pdfFile) {
        try {
          await uploadAtt.mutateAsync({ itemId: result.item.id, file: pdfFile });
        } catch (uploadErr) {
          console.warn('Attachment upload failed, but item was created:', uploadErr);
        }
      }

      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className={cn(
        'flex items-center gap-2 p-2.5 rounded-md border text-xs',
        confidenceBadge.cls,
      )}>
        <Check className="w-3.5 h-3.5 flex-shrink-0" />
        <span>Detected as <strong>{draft.docType.replace('_', ' ')}</strong> · {confidenceBadge.label}</span>
      </div>

      <div>
        <Label className="text-xs">Title</Label>
        <Input
          value={draft.title}
          onChange={(e) => onDraftChange({ ...draft, title: e.target.value })}
          className="mt-1"
        />
      </div>

      <div>
        <Label className="text-xs">Type</Label>
        <div className="flex gap-1 flex-wrap mt-1">
          {DOC_TYPES.map((d) => (
            <button
              key={d.value} type="button"
              onClick={() => onDraftChange({ ...draft, docType: d.value })}
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-medium transition-all',
                draft.docType === d.value
                  ? 'border-purple-500/60 bg-purple-500/10 text-purple-700 dark:text-purple-300'
                  : 'border-border text-muted-foreground',
              )}
            >
              <span>{d.emoji}</span><span>{d.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs">Folder</Label>
        <select
          value={folderId ?? ''}
          onChange={(e) => setFolderId(e.target.value || null)}
          className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
        >
          <option value="">No folder</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>{f.icon ? `${f.icon} ` : ''}{f.name}</option>
          ))}
        </select>
      </div>

      <div>
        <Label className="text-xs">Tags</Label>
        <Input
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          placeholder="Comma-separated"
          className="mt-1"
        />
      </div>

      {/* Detected metadata preview */}
      {Object.keys(draft.metadata).length > 0 && (
        <div className="bg-muted/40 rounded-md p-2 space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Auto-detected details
          </Label>
          {Object.entries(draft.metadata).map(([k, v]) => (
            <div key={k} className="text-[11px] flex items-center gap-1.5">
              <FileCheck2 className="w-2.5 h-2.5 text-emerald-600" />
              <span className="font-semibold capitalize">{k.replace(/([A-Z])/g, ' $1').toLowerCase()}:</span>
              <span className="text-muted-foreground">{String(v)}</span>
            </div>
          ))}
        </div>
      )}

      {draft.body && (
        <div>
          <Label className="text-xs flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> Extracted text
            <span className="text-muted-foreground font-normal">({draft.body.length.toLocaleString()} chars)</span>
          </Label>
          <textarea
            value={draft.body}
            onChange={(e) => onDraftChange({ ...draft, body: e.target.value })}
            className="w-full mt-1 min-h-[120px] max-h-[240px] rounded-md border border-border bg-background p-2 text-[11px] font-mono overflow-y-auto"
          />
        </div>
      )}

      {pdfFile && (
        <div className="flex items-center gap-2 text-[11px] bg-muted/40 rounded-md p-2">
          <FileUp className="w-3 h-3 text-purple-600" />
          <span>PDF will be attached: <strong>{pdfFile.name}</strong></span>
        </div>
      )}

      {draft.tags.length > 0 && (
        <div className="flex gap-1 flex-wrap">
          {draft.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]">#{t}</Badge>)}
        </div>
      )}

      {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

      <div className="flex gap-2 pt-2 border-t border-border">
        <Button
          onClick={handleSave} disabled={isSaving}
          className="bg-purple-600 hover:bg-purple-700 text-white flex-1"
        >
          {isSaving ? 'Saving…' : 'Save to Library'}
        </Button>
        <Button variant="outline" onClick={onBack}>← Back</Button>
      </div>
    </>
  );
}

// ============================================================
// Helpers
// ============================================================

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') { reject(new Error('bad_read')); return; }
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read_error'));
    reader.readAsDataURL(file);
  });
}
